import {
  isActiveNeoAttackStoreStatus,
  NEO_ATTACK_CACHE_TTL_MS,
  NEO_ATTACK_STARTUP_GRACE_MS,
  type NeoAttackStoreStatus,
} from './neoAttackStatus';

export type NeoAttackState = {
  threadId: string;
  executionId: string | null;
  status: NeoAttackStoreStatus;
  applicationId: string;
  startedAt: string;
  updatedAt: string;
};

export type BackendRunningNeoTrace = {
  signalId: string;
  threadId: string;
};

export type SerialQueueReconcileSnapshot = {
  signalIds: ReadonlySet<string>;
  activeSignalId: string | null;
};

export type ReconcileNeoAttacksParams = {
  applicationId: string;
  cachedEntries: Map<string, NeoAttackState>;
  backendRunningTraces: BackendRunningNeoTrace[];
  nowMs: number;
  startupGraceMs?: number;
  serialQueue?: SerialQueueReconcileSnapshot;
};

export type ReconcileNeoAttacksResult = {
  nextEntries: Map<string, NeoAttackState>;
};

export function createNeoAttackState({
  threadId,
  executionId,
  status,
  applicationId,
  startedAt,
  updatedAt,
}: {
  threadId: string;
  executionId: string | null;
  status: NeoAttackStoreStatus;
  applicationId: string;
  startedAt?: string;
  updatedAt?: string;
}): NeoAttackState {
  const timestamp = updatedAt ?? startedAt ?? new Date().toISOString();

  return {
    threadId,
    executionId,
    status,
    applicationId,
    startedAt: startedAt ?? timestamp,
    updatedAt: timestamp,
  };
}

export function normalizePersistedNeoAttackState(
  state: Partial<NeoAttackState>,
  fallbackTimestamp: string,
): NeoAttackState {
  return createNeoAttackState({
    threadId: state.threadId ?? '',
    executionId: state.executionId ?? null,
    status: state.status ?? 'starting',
    applicationId: state.applicationId ?? '',
    startedAt: state.startedAt ?? fallbackTimestamp,
    updatedAt: state.updatedAt ?? state.startedAt ?? fallbackTimestamp,
  });
}

export function isWithinNeoAttackCacheTtl({
  state,
  nowMs,
  ttlMs = NEO_ATTACK_CACHE_TTL_MS,
}: {
  state: NeoAttackState;
  nowMs: number;
  ttlMs?: number;
}): boolean {
  const updatedAtMs = new Date(state.updatedAt).getTime();

  if (Number.isNaN(updatedAtMs)) {
    return false;
  }

  return nowMs - updatedAtMs <= ttlMs;
}

export function shouldPersistNeoAttackState({
  state,
  nowMs,
}: {
  state: NeoAttackState;
  nowMs: number;
}): boolean {
  return (
    isActiveNeoAttackStoreStatus(state.status) &&
    isWithinNeoAttackCacheTtl({ state, nowMs })
  );
}

export function reconcileNeoAttacks({
  applicationId,
  cachedEntries,
  backendRunningTraces,
  nowMs,
  startupGraceMs = NEO_ATTACK_STARTUP_GRACE_MS,
  serialQueue,
}: ReconcileNeoAttacksParams): ReconcileNeoAttacksResult {
  const nextEntries = new Map(cachedEntries);
  const runningThreadIds = new Set(
    backendRunningTraces.map((trace) => trace.threadId),
  );
  const fallbackTimestamp = new Date(nowMs).toISOString();

  for (const trace of backendRunningTraces) {
    const existing = nextEntries.get(trace.signalId);

    if (!existing) {
      nextEntries.set(
        trace.signalId,
        createNeoAttackState({
          threadId: trace.threadId,
          executionId: null,
          status: 'running',
          applicationId,
          startedAt: fallbackTimestamp,
          updatedAt: fallbackTimestamp,
        }),
      );
      continue;
    }

    nextEntries.set(trace.signalId, {
      ...existing,
      threadId: trace.threadId,
      status: 'running',
      applicationId: existing.applicationId || applicationId,
      updatedAt: fallbackTimestamp,
    });
  }

  for (const [signalId, attack] of nextEntries.entries()) {
    if (attack.status === 'queued') {
      if (
        !serialQueue ||
        !attack.applicationId ||
        attack.applicationId !== applicationId
      ) {
        continue;
      }

      const stillWaiting =
        serialQueue.signalIds.has(signalId) ||
        serialQueue.activeSignalId === signalId;

      if (stillWaiting) {
        continue;
      }

      const queuedAtMs = new Date(attack.startedAt).getTime();

      if (Number.isNaN(queuedAtMs) || nowMs - queuedAtMs <= startupGraceMs) {
        continue;
      }

      nextEntries.set(signalId, {
        ...attack,
        status: 'failed',
        updatedAt: fallbackTimestamp,
      });
      continue;
    }

    if (!isActiveNeoAttackStoreStatus(attack.status)) {
      continue;
    }

    if (!attack.applicationId || attack.applicationId !== applicationId) {
      continue;
    }

    if (runningThreadIds.has(attack.threadId)) {
      continue;
    }

    const startedAtMs = new Date(attack.startedAt).getTime();

    if (Number.isNaN(startedAtMs) || nowMs - startedAtMs <= startupGraceMs) {
      continue;
    }

    nextEntries.delete(signalId);
  }

  return { nextEntries };
}

export function hasActiveNeoAttacksForApplication({
  cachedEntries,
  applicationId,
}: {
  cachedEntries: Map<string, NeoAttackState>;
  applicationId: string;
}): boolean {
  for (const attack of cachedEntries.values()) {
    if (!isActiveNeoAttackStoreStatus(attack.status)) {
      continue;
    }

    if (attack.applicationId === applicationId) {
      return true;
    }
  }

  return false;
}
